import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import config, { AGORA_APP_ID } from '../../config';
import streamChatService from '../../services/stream-chat.service';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';

import { useJoin, RemoteUser, useRemoteUsers } from 'agora-rtc-react';
import AgoraProvider, { client } from '../../context/AgoraProvider';
import SendGiftsModal from '../../components/gifts/SendGiftsModal';
import { formatViewers } from './utils/format-number';
import DotSpinner from '../../components/shared/DotSpinner';

const InteractiveLiveStreaming = () => {
  useEffect(() => {
    client.setClientRole('audience');
  }, []);
  return (
    <AgoraProvider>
      <StreamViewer />
    </AgoraProvider>
  );
};

const NOTIFY_KEY = 'lastNotifyCloseTime';
const NOTIFY_DELAY = 5 * 60 * 1000;

const StreamViewer = () => {
  const token = useSelector((state) => state.auth.auth_token);
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  // Stream state
  const { id: streamId } = useParams();

  const [streamer, setStreamer] = useState({ id: null, name: null, photo: null });
  const [viewerCount, setViewerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Stream message
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isShowNotifyGiftMessage, setIsShowNotifyGiftMessage] = useState(() => {
    const lastClose = localStorage.getItem(NOTIFY_KEY + streamId);
    if (!lastClose) return true;
    return Date.now() - parseInt(lastClose, 10) > NOTIFY_DELAY;
  });
  const bottomRef = useRef();

  // Present gifts
  const [isOpenPresentShop, setIsOpenPresentShop] = useState(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Agora refs
  const [agoraToken, setAgoraToken] = useState('');
  const [agoraChannel, setAgoraChannel] = useState('');

  const calling = Boolean(agoraToken && agoraChannel);

  const { isLoading: isLoadingAgoraJoin } = useJoin(
    {
      appid: AGORA_APP_ID,
      channel: agoraChannel,
      token: agoraToken,
    },
    calling,
  );
  const remoteUsers = useRemoteUsers();
  const remoteUser = remoteUsers.find((u) => u.hasVideo);

  const isLoadStream = isLoadingAgoraJoin || isLoading;
  const isDisabledStream = !isLoadingAgoraJoin && !isLoading && !remoteUser;

  useEffect(() => {
    if (!streamId) return;
    initConnectToStream();
    const cleanAppChat = initConnectToChat();

    return () => {
      cleanAppChat();
    };
  }, [streamId]);

  const initConnectToStream = async () => {
    setIsLoading(true);
    try {
      await fetchAgoraToken(streamId);
      await fetchJoinToStream(streamId);
    } catch (error) {
      console.error('[StreamViewer] Error connect to stream:', error);
      toast.error('Не удалось подключиться к стриму');
    } finally {
      setIsLoading(false);
    }
  };

  const initConnectToChat = () => {
    try {
      console.log('[StreamViewer] Initializing chat...');
      // Connect to stream chat service
      const authToken = token || localStorage.getItem('auth_token');
      streamChatService.connect(authToken);
      console.log('[StreamViewer] Connected to chat service');

      // Set up message listener
      const messageUnsubscribe = streamChatService.onMessage((data) => {
        console.log('[StreamViewer] Received message:', data);
        if (data.type === 'message' && data.data) {
          const formattedMessage = {
            _id: data.data._id || Date.now(),
            username: data.data.username || 'User',
            signed_thumbnail: data.data.signed_thumbnail,
            message: data.data.message,
            timestamp: new Date(data.data.timestamp || Date.now()).toLocaleTimeString(),
            role: data.data.role,
          };
          setChatMessages((prev) => [...prev, formattedMessage]);
        }
      });

      // Set up history listener
      const historyUnsubscribe = streamChatService.onHistory((data) => {
        console.log('[StreamViewer] Received chat history:', data);
        if (data?.messages) {
          const formattedMessages = data.messages.reverse();
          setChatMessages(formattedMessages);
        }
      });

      // Set up viewer count listener
      const viewerCountUnsubscribe = streamChatService.onViewerCount((data) => {
        console.log('[StreamViewer] Viewer count updated:', data);
        if (data.currentViewers !== undefined) {
          setViewerCount(data.currentViewers);
        }
      });

      // Set up gift listener
      const giftUnsubscribe = streamChatService.onGift((data) => {
        console.log('[StreamViewer] Gift received:', data);
        setChatMessages((prev) => [...prev, data.message]);
      });

      // Set up error listener
      const errorUnsubscribe = streamChatService.onError((error) => {
        console.error('[StreamViewer] Chat error:', error);
        toast.error(error.message || 'Chat error occurred');
      });

      // Join stream chat - используем новый метод
      console.log('[StreamViewer] Joining stream:', streamId);
      streamChatService.joinStream(streamId);

      // Cleanup on unmount
      return () => {
        console.log('[StreamViewer] Cleaning up chat listeners');
        messageUnsubscribe();
        historyUnsubscribe();
        viewerCountUnsubscribe();
        giftUnsubscribe();
        errorUnsubscribe();
      };
    } catch (error) {
      console.error('[StreamViewer] Error initializing chat:', error);
      toast.error('Failed to initialize chat');
    }
  };

  const fetchAgoraToken = async (streamId) => {
    try {
      const res = await axiosPrivate.get(`/stream/${streamId}/token`).then((res) => res.data);
      setAgoraToken(res.data.token);
      setAgoraChannel(res.data.channel);
    } catch (error) {
      console.error('[StreamViewer] Error fetchAgoraToken:', error);
    }
  };

  const fetchJoinToStream = async (streamId) => {
    try {
      const res = await axiosPrivate.post(`/stream/${streamId}/join`).then((res) => res.data);

      setViewerCount(res.data.currentViewers);
      setStreamer({
        id: res.data.host._id,
        name: res.data.host.name,
        photo: res.data.host.photo,
      });
    } catch (error) {
      console.error('[StreamViewer] Error fetchJoinToStream:', error);
    }
  };

  const fetchLeaveToStream = async (streamId) => {
    if (!streamId) return;
    try {
      const res = await axiosPrivate.post(`/stream/${streamId}/leave`).then((res) => res.data);
    } catch (error) {
      console.error('[StreamViewer] Error fetchLeaveToStream:', error);
    }
  };

  const onSendMessage = async () => {
    console.log('[StreamViewer] Sending message:', messageInput);
    if (!messageInput.trim()) return;

    try {
      console.log('[StreamViewer] Sending message:', messageInput);
      // Используем новый API - userId больше не нужен, бэкенд берет из сокета
      streamChatService.sendMessage(streamId, messageInput);
      console.log('[StreamViewer] Message sent successfully');
      setMessageInput('');
    } catch (error) {
      console.error('[StreamViewer] Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const onSendGifts = async (giftId) => {
    try {
      streamChatService.sendGift(streamId, giftId);
    } catch (error) {
      console.error('[StreamViewer] Error sending gift:', error);
      toast.error(error.response.data.message || 'Ошибка отправки подарка');
    }
  };

  const onBackToStreamList = async () => {
    try {
      // await rtcClient.leave();
      await client.leave();
      if (streamChatService.isConnected()) {
        streamChatService.leaveStream(streamId);
        streamChatService.disconnect();
      }
      // await fetchLeaveToStream(streamId);
    } catch (error) {
      console.error('[StreamViewer] error to run onBackToStreamList:', error);
    } finally {
      navigate(-1);
    }
  };

  const onGoToStreamerProfile = () => {
    if (!streamer.id) return;
    navigate(config.ROUTES.STREAMS.STREAMER_PROFILE(streamer.id));
  };

  const handleCloseNotifyMessage = () => {
    setIsShowNotifyGiftMessage(false);
    localStorage.setItem(NOTIFY_KEY + streamId, Date.now().toString());

    // Таймер для автоматического включения после 5 минут
    setTimeout(() => {
      const lastClose = localStorage.getItem(NOTIFY_KEY + streamId);
      if (!lastClose) return;
      if (Date.now() - parseInt(lastClose, 10) >= NOTIFY_DELAY) {
        setIsShowNotifyGiftMessage(true);
      }
    }, NOTIFY_DELAY);
  };

  if (isLoadStream) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <>
      <SendGiftsModal
        isOpen={isOpenPresentShop}
        onClose={() => setIsOpenPresentShop(false)}
        renderTitle={() => (
          <h2 className="text-[18px] font-semibold text-white text-center leading-[1.5] font-raleway">
            Обозначь себя, сделав человеку приятно. Это шаг к новым знакомствам!
          </h2>
        )}
        onSend={onSendGifts}
      />
      <div className="relative w-screen h-full bg-[#022424] text-white">
        <div className="flex items-center gap-1 flex-wrap justify-between absolute top-5 right-4 left-4 z-10">
          <button
            onClick={onGoToStreamerProfile}
            className="flex items-center gap-2 bg-white/10 rounded-full px-2 py-[6px] min-w-[180px]"
          >
            <img src={streamer.photo} alt="avatar" className="object-cover size-[38px] rounded-full" />
            <div>
              <p className="text-xs font-semibold uppercase">{streamer.name}</p>
              <div className="flex items-center gap-1">
                <img src="/icons/eye.svg" alt="eye" />
                <p className="text-sm">{formatViewers(viewerCount)}</p>
              </div>
            </div>
          </button>
          <button className="p-4 rounded-full bg-white/10" onClick={onBackToStreamList}>
            <img src="/icons/close.svg" alt="close" />
          </button>
        </div>

        {remoteUser && <RemoteUser key={remoteUser.uid} user={remoteUser} style={{ width: '100%', height: '100%' }} />}
        {!isDisabledStream && (
          <div className="flex flex-col justify-end gap-4 absolute h-1/2 bottom-5 right-4 left-4 z-10">
            <ul className=" w-full overflow-auto space-y-3">
              {chatMessages.map((m) => (
                <li key={m._id} className="flex items-start gap-2">
                  <img src={m.signed_thumbnail} className="size-8 object-cover rounded-full shrink-0" alt="avatar" />
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate min-w-0">{m.username}</p>
                    {m.type !== 'gift' ? (
                      <p className="text-sm font-semibold break-words">{m.message}</p>
                    ) : (
                      <img src={m.gift.imageUrl} className="w-[40px] h-[40px]" />
                    )}
                  </div>
                </li>
              ))}
              <li ref={bottomRef} />
            </ul>
            <div className="relative flex justify-between gap-[10px]">
              <input
                type="text"
                placeholder="Оставить комментарий"
                className={'w-full h-full rounded-full px-4 py-[18px] text-lg focus:outline-none bg-white/20'}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                maxLength={100}
              />
              {messageInput.trim().length > 0 ? (
                <button className="shrink-0 p-5 rounded-full bg-[#A1F69E]" onClick={onSendMessage}>
                  <img src="/icons/send.svg" alt="send" />
                </button>
              ) : (
                <button onClick={() => setIsOpenPresentShop(true)} className="shrink-0 p-5 rounded-full bg-white/20">
                  <img src="/icons/gifts.svg" alt="gifts" />
                </button>
              )}
              {isShowNotifyGiftMessage && (
                <div className="absolute right-0 bottom-full mb-7 flex items-start gap-1 max-w-[200px] border border-white bg-black/40 backdrop-blur-[2px] rounded-2xl px-3 py-[6px]">
                  <p className="text-[13px] font-semibold">
                    <span className="text-[#A1F69E]">Отправь комплимент,</span> чтобы стать заметнее и написать в личные
                    сообщения стримеру!
                  </p>
                  <button className="shrink-0 p-2 rounded-full bg-white/10" onClick={handleCloseNotifyMessage}>
                    <img className="size-2" src="/icons/close.svg" alt="close" />
                  </button>
                  <div className="absolute right-6 top-[calc(100%-7.5px)] w-4 h-4 rotate-45 border-r border-b border-white bg-black/40 backdrop-blur-[2px] [clip-path:polygon(0_100%,100%_100%,100%_0)]" />
                </div>
              )}
            </div>
          </div>
        )}
        {isDisabledStream && (
          <div className="absolute inset-0 flex items-center justify-center w-full bg-black/80 text-2xl">
            Стрим завершен
          </div>
        )}
      </div>
    </>
  );
};

export default InteractiveLiveStreaming;
